import { createFileRoute, Link } from "@tanstack/react-router";

import problemsPhotoSrc from "@/assets/problemswesolve.png";
const problemsPhoto = { url: problemsPhotoSrc };
import boardroomAssetSrc from "@/assets/boardroom.png";
const boardroomAsset = { url: boardroomAssetSrc };
import { AdvisorCTA } from "@/components/site/AdvisorCTA";
import { problems } from "@/content/problems";
import { problemCardCopy as cardCopy } from "@/content/problem-cards";
import { SITE_URL } from "@/lib/seo";

export const Route = createFileRoute("/problems/")({
  head: () => ({
    links: [{ rel: "canonical", href: `${SITE_URL}/problems/` }],
    meta: [
      { title: "Problems We Solve | Leadership Edge Live" },
      {
        name: "description",
        content:
          "New supervisors drowning, hard conversations postponed, change that stalls. Pick the problem that sounds like your week and see what solves it.",
      },
      { property: "og:title", content: "Problems We Solve | Leadership Edge Live" },
      {
        property: "og:description",
        content:
          "Start with what's actually going wrong. Six common leadership problems, and the courses that solve them.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: ProblemsIndex,
});

function ProblemsIndex() {
  return (
    <>
      <section className="relative isolate overflow-hidden">
        {/* Full-bleed hero photo with a light overlay for text legibility */}
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <img
            src={problemsPhoto.url}
            alt=""
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          {/* Mobile: broad scrim so dark text stays legible over the photo */}
          <div className="absolute inset-0 bg-white/85 md:hidden" />
          <div
            className="absolute inset-0 hidden md:block"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 50%, rgba(255,255,255,0) 100%)",
            }}
          />
        </div>
        <div className="relative mx-auto flex min-h-[620px] max-w-6xl items-center px-6 py-16 md:py-20">
          <div className="max-w-[600px]">
            <h1
              className="font-sans text-4xl font-bold leading-[1.05] tracking-tight text-[#111111] sm:text-5xl lg:text-6xl"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Start with what&apos;s actually going wrong.
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-muted-foreground">
              Your managers aren&apos;t failing on purpose. Pick the problem that sounds like your
              week and see exactly what fixes it.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/course-catalog/"
                className="inline-flex items-center justify-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#693D94]/90"
              >
                View the Course Catalog
              </Link>
              <Link
                to="/contact/"
                className="inline-flex items-center justify-center rounded-[6px] border border-black/40 bg-transparent px-6 py-3 text-sm font-semibold text-black transition-colors hover:border-black/60 hover:bg-black/5"
              >
                Talk to a Development Pro
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-20 md:py-24">
        <h2
          className="text-[1.3125rem] font-bold leading-[1.1] tracking-tight text-[#111111] sm:text-[1.575rem] lg:text-[2.1rem]"
          style={{ fontFamily: "'Poppins', sans-serif" }}
        >
          Six problems we hear most
        </h2>
        <ul className="mt-12 border-t border-border/60">
          {problems.map((p) => {
            const copy = cardCopy[p.slug];
            return (
              <li key={p.slug} className="border-b border-border/60">
                <Link
                  to="/problems/$slug/"
                  params={{ slug: p.slug }}
                  className="group flex flex-col gap-4 py-6 sm:flex-row sm:items-center sm:gap-8 md:py-7"
                >
                  <div className="flex-1">
                    <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                      {p.eyebrow}
                    </span>
                    <h3
                      className="mt-3 text-xl font-bold leading-snug text-[#111111] transition-colors group-hover:text-[#693D94] md:text-2xl"
                      style={{ fontFamily: "'Poppins', sans-serif" }}
                    >
                      {copy?.title ?? p.headline}
                    </h3>
                    <p className="mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground">
                      {copy?.body ?? p.supporting}
                    </p>
                  </div>
                  <span className="inline-flex shrink-0 items-center justify-center self-start rounded-[6px] border border-[#693D94]/40 px-4 py-2 text-sm font-semibold text-[#693D94] transition-colors group-hover:border-[#693D94]/60 group-hover:bg-[#693D94]/5 sm:self-auto">
                    Explore
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </section>

      <AdvisorCTA
        backgroundImage={boardroomAsset.url}
        title="Not sure which one to start with?"
        primaryLabel="Take the Assessment"
        primaryTo="/leadership-gaps-assessment/"
        body="Take the Leadership Gaps Assessment, or talk to a Development Pro. We'll help you pick the first thing to fix."
      />
    </>
  );
}
